import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft, Image, Send, Save, RefreshCw, CheckCircle, 
  XCircle, AlertCircle, Clock, ChevronDown, Trash2, Edit2,
  MapPin, Link as LinkIcon, Plus, X
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import BottomNav from "@/components/BottomNav";
import { getSessionToken } from "@/utils/auth";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Status badge component
const StatusBadge = ({ status, platform }) => {
  const configs = {
    pending: { bg: "bg-yellow-100", text: "text-yellow-700", icon: Clock, label: "Pending" },
    success: { bg: "bg-green-100", text: "text-green-700", icon: CheckCircle, label: "Posted" },
    failed: { bg: "bg-red-100", text: "text-red-700", icon: XCircle, label: "Failed" },
    not_configured: { bg: "bg-zinc-100", text: "text-zinc-500", icon: AlertCircle, label: "Not Configured" },
  };
  
  const config = configs[status] || configs.pending;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon className="w-3 h-3" />
      {platform}: {config.label}
    </span>
  );
};

export default function JobUpdates() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(true);
  
  // Data state
  const [updates, setUpdates] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [platformStatus, setPlatformStatus] = useState({});
  
  // Composer state
  const [showComposer, setShowComposer] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState(null);
  const [caption, setCaption] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("CA");
  // Service count fields
  const [windowRescreens, setWindowRescreens] = useState("");
  const [newWindowScreens, setNewWindowScreens] = useState("");
  const [screenDoorRescreens, setScreenDoorRescreens] = useState("");
  const [newScreenDoors, setNewScreenDoors] = useState("");
  const [customService, setCustomService] = useState("");
  // PetScreen checkboxes
  const [rescreenPetScreen, setRescreenPetScreen] = useState(false);
  const [newDoorPetScreen, setNewDoorPetScreen] = useState(false);
  const [ctaUrl, setCtaUrl] = useState("https://jobs.screenzz.com/instant-estimate");
  const [images, setImages] = useState([]);
  const [postToGoogle, setPostToGoogle] = useState(true);
  const [postToFacebook, setPostToFacebook] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [publishing, setPublishing] = useState(false);
  
  // Filter state
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    if (location.state?.user) return;
    
    const fetchUser = async () => {
      try {
        const token = getSessionToken();
        if (!token) {
          navigate("/login");
          return;
        }
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.role !== "Admin") {
          toast.error("Access denied - Admin only");
          navigate("/jobs");
          return;
        }
        setUser(response.data);
      } catch (error) {
        navigate("/login");
      }
    };
    fetchUser();
  }, [navigate, location.state?.user]);

  useEffect(() => {
    if (user) {
      fetchUpdates();
      fetchTemplates();
      fetchPlatformStatus();
      
      // Check for OAuth callback parameters
      const urlParams = new URLSearchParams(window.location.search);
      const googleSuccess = urlParams.get('google_success');
      const googleError = urlParams.get('google_error');
      
      if (googleSuccess === 'true') {
        toast.success("Google Business Profile connected successfully!");
        // Clean up URL
        window.history.replaceState({}, '', '/settings/marketing/job-updates');
        // Refresh platform status
        fetchPlatformStatus();
      } else if (googleError) {
        let errorMessage = "Failed to connect Google Business Profile";
        if (googleError === 'no_refresh_token') {
          errorMessage = "Google didn't provide a refresh token. Please try again or revoke access in your Google account settings first.";
        } else if (googleError === 'no_code') {
          errorMessage = "No authorization code received from Google.";
        } else {
          errorMessage = `Google OAuth error: ${googleError}`;
        }
        toast.error(errorMessage);
        // Clean up URL
        window.history.replaceState({}, '', '/settings/marketing/job-updates');
      }
    }
  }, [user]);

  const handleConnectGoogle = async () => {
    try {
      const token = getSessionToken();
      const response = await axios.get(`${API}/google-auth/start`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.auth_url) {
        // Redirect to Google OAuth consent screen
        window.location.href = response.data.auth_url;
      } else {
        toast.error("Failed to start Google authorization");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start Google authorization");
    }
  };

  const fetchUpdates = async () => {
    try {
      setLoading(true);
      const token = getSessionToken();
      const response = await axios.get(`${API}/job-updates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUpdates(response.data);
    } catch (error) {
      console.error("Failed to fetch job updates:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = getSessionToken();
      const response = await axios.get(`${API}/job-updates/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates);
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    }
  };

  const fetchPlatformStatus = async () => {
    try {
      const token = getSessionToken();
      const response = await axios.get(`${API}/job-updates/platform-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlatformStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch platform status:", error);
    }
  };

  const applyTemplate = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;
    
    // Calculate totals for template
    const totalWindowScreens = (parseInt(windowRescreens) || 0) + (parseInt(newWindowScreens) || 0);
    const totalScreenDoors = (parseInt(screenDoorRescreens) || 0) + (parseInt(newScreenDoors) || 0);
    
    let text = template.template;
    text = text.replace("{window_screens}", totalWindowScreens > 0 ? totalWindowScreens : "[#]");
    text = text.replace("{screen_doors}", totalScreenDoors > 0 ? totalScreenDoors : "[#]");
    text = text.replace("{city}", city || "[City]");
    text = text.replace("{state}", state || "CA");
    
    // Add custom service if provided
    if (customService.trim()) {
      text += `\n\n${customService.trim()}`;
    }
    
    // Add PetScreen mention if applicable
    if (rescreenPetScreen || newDoorPetScreen) {
      text += "\n\n🐾 Upgraded with PetScreen for extra durability!";
    }
    
    setCaption(text);
    setSelectedTemplate(templateId);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (images.length + files.length > 5) {
      toast.error("Maximum 5 images allowed");
      return;
    }
    
    const formData = new FormData();
    files.forEach(file => formData.append("images", file));
    
    try {
      const token = getSessionToken();
      const response = await axios.post(`${API}/job-updates/upload-images`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      setImages([...images, ...response.data.images]);
      toast.success(`${response.data.images.length} image(s) uploaded`);
    } catch (error) {
      toast.error("Failed to upload images");
    }
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const resetComposer = () => {
    setCaption("");
    setCity("");
    setState("CA");
    setWindowRescreens("");
    setNewWindowScreens("");
    setScreenDoorRescreens("");
    setNewScreenDoors("");
    setCustomService("");
    setRescreenPetScreen(false);
    setNewDoorPetScreen(false);
    setCtaUrl("https://jobs.screenzz.com/instant-estimate");
    setImages([]);
    setPostToGoogle(true);
    setPostToFacebook(true);
    setSelectedTemplate("");
    setEditingUpdate(null);
    setShowComposer(false);
  };

  const handleSaveDraft = async () => {
    if (!caption.trim() || !city.trim()) {
      toast.error("Caption and City are required");
      return;
    }
    
    try {
      const token = getSessionToken();
      const payload = {
        caption,
        city,
        state,
        window_rescreens: windowRescreens ? parseInt(windowRescreens) : null,
        new_window_screens: newWindowScreens ? parseInt(newWindowScreens) : null,
        screen_door_rescreens: screenDoorRescreens ? parseInt(screenDoorRescreens) : null,
        new_screen_doors: newScreenDoors ? parseInt(newScreenDoors) : null,
        custom_service: customService.trim() || null,
        rescreen_petscreen: rescreenPetScreen,
        new_door_petscreen: newDoorPetScreen,
        images,
        cta_url: ctaUrl,
        post_to_google: postToGoogle,
        post_to_facebook: postToFacebook,
        is_draft: true
      };
      
      if (editingUpdate) {
        await axios.put(`${API}/job-updates/${editingUpdate.update_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Draft updated");
      } else {
        await axios.post(`${API}/job-updates`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Draft saved");
      }
      
      resetComposer();
      fetchUpdates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save draft");
    }
  };

  const handlePublish = async () => {
    if (!caption.trim() || !city.trim()) {
      toast.error("Caption and City are required");
      return;
    }
    
    if (!postToGoogle && !postToFacebook) {
      toast.error("Select at least one platform to post to");
      return;
    }
    
    setPublishing(true);
    
    try {
      const token = getSessionToken();
      const payload = {
        caption,
        city,
        state,
        window_rescreens: windowRescreens ? parseInt(windowRescreens) : null,
        new_window_screens: newWindowScreens ? parseInt(newWindowScreens) : null,
        screen_door_rescreens: screenDoorRescreens ? parseInt(screenDoorRescreens) : null,
        new_screen_doors: newScreenDoors ? parseInt(newScreenDoors) : null,
        custom_service: customService.trim() || null,
        rescreen_petscreen: rescreenPetScreen,
        new_door_petscreen: newDoorPetScreen,
        images,
        cta_url: ctaUrl,
        post_to_google: postToGoogle,
        post_to_facebook: postToFacebook,
        is_draft: false
      };
      
      let response;
      if (editingUpdate) {
        // First update, then publish
        await axios.put(`${API}/job-updates/${editingUpdate.update_id}`, {...payload, is_draft: true}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        response = await axios.post(`${API}/job-updates/${editingUpdate.update_id}/publish`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.post(`${API}/job-updates`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      const result = response.data;
      
      // Check posting results
      let successCount = 0;
      let failedPlatforms = [];
      
      if (postToGoogle) {
        if (result.google_status === "success") successCount++;
        else if (result.google_status !== "not_configured") failedPlatforms.push("Google");
      }
      if (postToFacebook) {
        if (result.facebook_status === "success") successCount++;
        else if (result.facebook_status !== "not_configured") failedPlatforms.push("Facebook");
      }
      
      if (failedPlatforms.length > 0) {
        toast.warning(`Update created. ${failedPlatforms.join(" and ")} posting needs configuration.`);
      } else {
        toast.success("Job update published!");
      }
      
      resetComposer();
      fetchUpdates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to publish");
    } finally {
      setPublishing(false);
    }
  };

  const handleRetry = async (updateId, platform) => {
    try {
      const token = getSessionToken();
      await axios.post(`${API}/job-updates/${updateId}/retry/${platform}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Retrying ${platform} posting...`);
      fetchUpdates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Retry failed");
    }
  };

  const handleDelete = async (updateId) => {
    if (!confirm("Are you sure you want to delete this job update?")) return;
    
    try {
      const token = getSessionToken();
      await axios.delete(`${API}/job-updates/${updateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Job update deleted");
      fetchUpdates();
    } catch (error) {
      toast.error("Failed to delete job update");
    }
  };

  const handleEdit = (update) => {
    setEditingUpdate(update);
    setCaption(update.caption);
    setCity(update.city);
    setState(update.state);
    setWindowRescreens(update.window_rescreens?.toString() || "");
    setNewWindowScreens(update.new_window_screens?.toString() || "");
    setScreenDoorRescreens(update.screen_door_rescreens?.toString() || "");
    setNewScreenDoors(update.new_screen_doors?.toString() || "");
    setCustomService(update.custom_service || "");
    setRescreenPetScreen(update.rescreen_petscreen || false);
    setNewDoorPetScreen(update.new_door_petscreen || false);
    setCtaUrl(update.cta_url);
    setImages(update.images || []);
    setPostToGoogle(update.post_to_google);
    setPostToFacebook(update.post_to_facebook);
    setShowComposer(true);
  };

  const publishDraft = async (updateId) => {
    try {
      const token = getSessionToken();
      await axios.post(`${API}/job-updates/${updateId}/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Draft published!");
      fetchUpdates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to publish");
    }
  };

  const filteredUpdates = updates.filter(u => {
    if (filterStatus === "all") return true;
    if (filterStatus === "draft") return u.is_draft;
    if (filterStatus === "published") return !u.is_draft;
    return true;
  });

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-zinc-200 border-t-[#207024]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 pb-24">
      <Header user={user} />
      
      <div className="max-w-4xl mx-auto p-4">
        {/* Header with back button */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate("/settings", { state: { user } })}
            className="p-2 rounded-lg hover:bg-zinc-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Job Updates</h1>
            <p className="text-sm text-zinc-500">Publish job updates to Google Business Profile and Facebook</p>
          </div>
        </div>

        {/* Platform Status Cards */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className={`p-4 rounded-xl border ${platformStatus.google_business?.configured ? 'bg-green-50 border-green-200' : platformStatus.google_business?.partial_config ? 'bg-amber-50 border-amber-200' : 'bg-zinc-50 border-zinc-200'}`}>
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-2 h-2 rounded-full ${platformStatus.google_business?.configured ? 'bg-green-500' : platformStatus.google_business?.partial_config ? 'bg-amber-500' : 'bg-zinc-400'}`} />
              <span className="font-semibold text-sm">Google Business</span>
            </div>
            <p className="text-xs text-zinc-500 mb-2">
              {platformStatus.google_business?.configured 
                ? "Ready to post" 
                : platformStatus.google_business?.partial_config 
                  ? "Click to connect" 
                  : "Not configured"}
            </p>
            {platformStatus.google_business?.partial_config && !platformStatus.google_business?.configured && (
              <button
                onClick={handleConnectGoogle}
                className="w-full py-2 px-3 bg-amber-500 text-white text-xs font-semibold rounded-lg hover:bg-amber-600 transition-colors"
              >
                Connect Google
              </button>
            )}
          </div>
          <div className={`p-4 rounded-xl border ${platformStatus.facebook?.configured ? 'bg-green-50 border-green-200' : 'bg-zinc-50 border-zinc-200'}`}>
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-2 h-2 rounded-full ${platformStatus.facebook?.configured ? 'bg-green-500' : 'bg-zinc-400'}`} />
              <span className="font-semibold text-sm">Facebook Page</span>
            </div>
            <p className="text-xs text-zinc-500">
              {platformStatus.facebook?.configured 
                ? platformStatus.facebook?.page_name || "Ready to post"
                : "Not configured"}
            </p>
          </div>
        </div>

        {/* New Update Button */}
        {!showComposer && (
          <button
            onClick={() => setShowComposer(true)}
            data-testid="create-job-update-btn"
            className="w-full mb-6 py-4 bg-[#207024] text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-[#1a5c1d] transition-colors"
          >
            <Plus className="w-5 h-5" />
            Create Job Update
          </button>
        )}

        {/* Composer */}
        {showComposer && (
          <div className="bg-white rounded-xl border p-4 mb-6" data-testid="job-update-composer">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-lg">
                {editingUpdate ? "Edit Job Update" : "New Job Update"}
              </h2>
              <button onClick={resetComposer} className="p-2 hover:bg-zinc-100 rounded-lg" data-testid="close-composer-btn">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Template Selector */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-zinc-700 mb-1">Caption Template</label>
              <select
                value={selectedTemplate}
                onChange={(e) => applyTemplate(e.target.value)}
                data-testid="template-select"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#207024] focus:border-transparent"
              >
                <option value="">Select a template...</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            {/* Service counts for template variables */}
            <div className="mb-4 p-4 bg-zinc-50 rounded-lg">
              <label className="block text-sm font-semibold text-zinc-700 mb-3">Service Details (for templates)</label>
              
              {/* Window Screens */}
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-xs font-medium text-zinc-600 mb-1">Window Rescreens</label>
                  <input
                    type="number"
                    min="0"
                    value={windowRescreens}
                    onChange={(e) => setWindowRescreens(e.target.value)}
                    placeholder="0"
                    data-testid="window-rescreens-input"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#207024]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-600 mb-1">New Window Screens</label>
                  <input
                    type="number"
                    min="0"
                    value={newWindowScreens}
                    onChange={(e) => setNewWindowScreens(e.target.value)}
                    placeholder="0"
                    data-testid="new-window-screens-input"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#207024]"
                  />
                </div>
              </div>

              {/* Screen Door Rescreens */}
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-xs font-medium text-zinc-600 mb-1">Screen Door Rescreens</label>
                  <input
                    type="number"
                    min="0"
                    value={screenDoorRescreens}
                    onChange={(e) => setScreenDoorRescreens(e.target.value)}
                    placeholder="0"
                    data-testid="screen-door-rescreens-input"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#207024]"
                  />
                  <label className="flex items-center gap-2 mt-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rescreenPetScreen}
                      onChange={(e) => setRescreenPetScreen(e.target.checked)}
                      data-testid="rescreen-petscreen-checkbox"
                      className="w-4 h-4 text-[#207024] rounded focus:ring-[#207024]"
                    />
                    <span className="text-xs text-zinc-600">PetScreen Upgrade?</span>
                  </label>
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-600 mb-1">New Screen Doors</label>
                  <input
                    type="number"
                    min="0"
                    value={newScreenDoors}
                    onChange={(e) => setNewScreenDoors(e.target.value)}
                    placeholder="0"
                    data-testid="new-screen-doors-input"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#207024]"
                  />
                  <label className="flex items-center gap-2 mt-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newDoorPetScreen}
                      onChange={(e) => setNewDoorPetScreen(e.target.checked)}
                      data-testid="new-door-petscreen-checkbox"
                      className="w-4 h-4 text-[#207024] rounded focus:ring-[#207024]"
                    />
                    <span className="text-xs text-zinc-600">PetScreen Upgrade?</span>
                  </label>
                </div>
              </div>

              {/* Custom Service */}
              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">Custom (optional)</label>
                <input
                  type="text"
                  value={customService}
                  onChange={(e) => setCustomService(e.target.value)}
                  placeholder="e.g., Security screen install, pet door, etc."
                  data-testid="custom-service-input"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#207024]"
                />
              </div>
            </div>

            {/* Caption */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-zinc-700 mb-1">Caption *</label>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={5}
                placeholder="Write your job update caption..."
                data-testid="caption-textarea"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#207024] resize-none"
              />
            </div>

            {/* Location */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  City *
                </label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="e.g., Murrieta"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#207024]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">State</label>
                <input
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  placeholder="CA"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#207024]"
                />
              </div>
            </div>

            {/* CTA URL */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                <LinkIcon className="w-4 h-4 inline mr-1" />
                CTA URL
              </label>
              <input
                type="url"
                value={ctaUrl}
                onChange={(e) => setCtaUrl(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#207024]"
              />
            </div>

            {/* Image Upload */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                <Image className="w-4 h-4 inline mr-1" />
                Photos ({images.length}/5)
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {images.map((img, idx) => (
                  <div key={idx} className="relative w-20 h-20">
                    <img 
                      src={img.startsWith('/api') ? `${BACKEND_URL}${img}` : img} 
                      alt={`Upload ${idx + 1}`}
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <button
                      onClick={() => removeImage(idx)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {images.length < 5 && (
                  <label className="w-20 h-20 border-2 border-dashed border-zinc-300 rounded-lg flex items-center justify-center cursor-pointer hover:bg-zinc-50">
                    <Plus className="w-6 h-6 text-zinc-400" />
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Platform Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-zinc-700 mb-2">Post to:</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={postToGoogle}
                    onChange={(e) => setPostToGoogle(e.target.checked)}
                    className="w-4 h-4 text-[#207024] rounded focus:ring-[#207024]"
                  />
                  <span className="text-sm">Google Business Profile</span>
                  {!platformStatus.google_business?.configured && (
                    <span className="text-xs text-zinc-400">(not configured)</span>
                  )}
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={postToFacebook}
                    onChange={(e) => setPostToFacebook(e.target.checked)}
                    className="w-4 h-4 text-[#207024] rounded focus:ring-[#207024]"
                  />
                  <span className="text-sm">Facebook Page</span>
                  {!platformStatus.facebook?.configured && (
                    <span className="text-xs text-zinc-400">(not configured)</span>
                  )}
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleSaveDraft}
                data-testid="save-draft-btn"
                className="flex-1 py-3 border border-zinc-300 rounded-xl font-semibold text-zinc-700 flex items-center justify-center gap-2 hover:bg-zinc-50"
              >
                <Save className="w-4 h-4" />
                Save as Draft
              </button>
              <button
                onClick={handlePublish}
                disabled={publishing}
                data-testid="publish-btn"
                className="flex-1 py-3 bg-[#207024] text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-[#1a5c1d] disabled:opacity-50"
              >
                {publishing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Publish Now
              </button>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-4" data-testid="filter-tabs">
          {[
            { value: "all", label: "All" },
            { value: "published", label: "Published" },
            { value: "draft", label: "Drafts" }
          ].map(tab => (
            <button
              key={tab.value}
              onClick={() => setFilterStatus(tab.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterStatus === tab.value
                  ? "bg-[#207024] text-white"
                  : "bg-white border text-zinc-600 hover:bg-zinc-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Updates List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-zinc-200 border-t-[#207024]"></div>
          </div>
        ) : filteredUpdates.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border">
            <Send className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
            <p className="text-zinc-500">No job updates yet</p>
            <p className="text-sm text-zinc-400 mt-1">Create your first update above</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredUpdates.map(update => (
              <div key={update.update_id} className="bg-white rounded-xl border p-4">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      {update.is_draft ? (
                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full font-medium">Draft</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">Published</span>
                      )}
                      <span className="text-xs text-zinc-400">
                        {new Date(update.created_at).toLocaleDateString('en-US', { 
                          month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-zinc-600">
                      <MapPin className="w-3 h-3" />
                      {update.city}, {update.state}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {update.is_draft && (
                      <>
                        <button
                          onClick={() => handleEdit(update)}
                          className="p-2 hover:bg-zinc-100 rounded-lg text-zinc-600"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => publishDraft(update.update_id)}
                          className="p-2 hover:bg-green-100 rounded-lg text-green-600"
                          title="Publish"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleDelete(update.update_id)}
                      className="p-2 hover:bg-red-100 rounded-lg text-red-600"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Images */}
                {update.images && update.images.length > 0 && (
                  <div className="flex gap-2 mb-3 overflow-x-auto">
                    {update.images.map((img, idx) => (
                      <img
                        key={idx}
                        src={img.startsWith('/api') ? `${BACKEND_URL}${img}` : img}
                        alt={`Image ${idx + 1}`}
                        className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                      />
                    ))}
                  </div>
                )}

                {/* Caption Preview */}
                <p className="text-sm text-zinc-700 whitespace-pre-wrap mb-3 line-clamp-3">
                  {update.caption}
                </p>

                {/* Platform Status */}
                {!update.is_draft && (
                  <div className="flex flex-wrap gap-2">
                    {update.post_to_google && (
                      <div className="flex items-center gap-2">
                        <StatusBadge status={update.google_status} platform="Google" />
                        {update.google_status === "failed" && (
                          <button
                            onClick={() => handleRetry(update.update_id, "google")}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Retry
                          </button>
                        )}
                      </div>
                    )}
                    {update.post_to_facebook && (
                      <div className="flex items-center gap-2">
                        <StatusBadge status={update.facebook_status} platform="Facebook" />
                        {update.facebook_status === "failed" && (
                          <button
                            onClick={() => handleRetry(update.update_id, "facebook")}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Retry
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Error Messages */}
                {update.google_error && update.google_status !== "success" && (
                  <div className="mt-2 p-2 bg-red-50 text-red-700 text-xs rounded">
                    Google: {update.google_error}
                  </div>
                )}
                {update.facebook_error && update.facebook_status !== "success" && (
                  <div className="mt-2 p-2 bg-red-50 text-red-700 text-xs rounded">
                    Facebook: {update.facebook_error}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNav user={user} />
    </div>
  );
}
